import React, { useEffect, useState, useContext } from "react";
import { useWeb3React } from "@web3-react/core";
import Web3 from "web3";
import { Avatar, Badge, Tooltip } from '@nextui-org/react';
import ContractContext from '../../ContractContext';
import BigNumber from 'bignumber.js';
import { getTokenLogo, formatDecimals, formatCurrencyExtended } from '../../utils';

const RewardPackProfile = ({ holderAddress, imageReferences, tokenSelections, setSubscriptions, showPercents = false }) => {
    const { library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    const [subscriptions, internalSetSubscriptions] = useState([]);
    const [subscriptionWeights, setSubscriptionWeights] = useState([]);
    const [unclaimedRewards, setUnclaimedRewards] = useState([]);

    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const web3 = new Web3(library.provider);
                const rewardPacksContract = contracts.rp_rewardPacks;
                const { _subscriptions, _subscriptionWeights } = await rewardPacksContract.methods.getHolderSubscriptions(holderAddress).call();

                internalSetSubscriptions(_subscriptions);
                setSubscriptionWeights(_subscriptionWeights);

                const rewards = await Promise.all(
                    _subscriptions.map(async (subscription) => {
                        const unclaimed = await rewardPacksContract.methods.getTotalUnrealisedWithRealisedUndeposited(holderAddress, subscription).call();
                        return { subscription, unclaimed: new BigNumber(unclaimed) };
                    })
                );

                setUnclaimedRewards(rewards);
                setSubscriptions(_subscriptions);  // Set subscriptions for parent component
            } catch (error) {
                console.error("Error fetching subscriptions:", error);
            }
        };

        if (holderAddress) {
            fetchSubscriptions();
        }
    }, [holderAddress, library, contracts, setSubscriptions]);

    const calculateTotalWeight = () => {
        return subscriptionWeights?.reduce((total, weight) => total + parseFloat(weight), 0);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
            <Avatar.Group>
                {subscriptions?.map((subscription, index) => {
                    const tokenInfo = tokenSelections?.find(t => t?.address?.toLowerCase() === subscription?.toLowerCase());
                    const logoUrl = getTokenLogo(imageReferences, tokenInfo);
                    const unclaimed = unclaimedRewards[index]?.unclaimed;
                    const weightPercentage = (parseFloat(subscriptionWeights[index]) / calculateTotalWeight()) * 100;

                    return (
                        <Tooltip
                            key={index}
                            content={
                                <div style={{
                                    fontSize: '11px',
                                }}>
                                    {unclaimed && tokenInfo && (
                                        <>Pending:
                                            <div>{formatDecimals(unclaimed, tokenInfo.decimals)} {tokenInfo.label} </div>
                                            <div>{formatCurrencyExtended(unclaimed.dividedBy(new BigNumber(10).pow(tokenInfo.decimals)).times(tokenInfo.price))}</div>
                                        </>
                                    )}
                                </div>
                            }
                            placement="bottom"
                        >
                            {showPercents ? (
                                <Badge disableOutline content={`${weightPercentage?.toFixed(2)}%`} size="xs">
                                    <Avatar
                                        size="md"
                                        pointer
                                        src={logoUrl}
                                        bordered
                                        color={unclaimed?.gt(0) ? "warning" : "gradient"}
                                        stacked
                                    />
                                </Badge>
                            ) : (
                                <Avatar
                                    size="md"
                                    pointer
                                    src={logoUrl}
                                    bordered
                                    color={unclaimed?.gt(0) ? "warning" : "gradient"}
                                    stacked
                                />
                            )}
                        </Tooltip>
                    );
                })}
            </Avatar.Group>
        </div>
    );
};

export default RewardPackProfile;
